import { DatapointStatus, logUsage, PerformanceDatapoint } from 'owa-analytics';
import { owaDate } from 'owa-datetime';
import { trace } from 'owa-trace';

import setAnonymousSharingStatus from '../actions/setAnonymousSharingStatus';
import setIsSaving, { setIsTurningOnBookingPage } from '../actions/setIsSaving';
import updatePublishingSettings, {
    updatePublishingSettingsIsPublished,
} from '../actions/updatePublishingSettings';
import getMeetingTypesFromServer from '../orchestrators/getMeetingTypesFromServer';
import getEntryPoint from '../selectors/getEntryPoint';
import {
    PUBLISHING_SETTINGS_ID,
    PUBLISHING_SETTINGS_NAME,
} from '../selectors/getPublishSettingsVariables';
import initializePublishSettings from '../selectors/initializePublishSettings';
import getMailboxPuid from '../selectors/getMailboxPuid';
import getMeetingTypeExtension from '../services/getMeetingTypeExtension';
import setPersonalSchedulingPage from '../services/setPersonalSchedulingPage';
import updateMeetingTypeExtension from '../services/updateMeetingTypeExtension';
import { PublishStatus } from '../store/schema/BookWithMeOrganizerStore';
import store from '../store/Store';
import { getUserMailboxGuid } from './getMailboxGuid';

import type PublishingSettings from 'owa-service/lib/contract/PublishingSettings';
import type MeetingTypeExtension from '../services/schema/MeetingTypeExtension';
import type { PublishingSettingsExtension } from '../store/schema/PublishingSettingsExtension';
import type MeetingTypeExtensionResponse from '../services/schema/MeetingTypeExtensionResponse';
import createDefaultMeetingTypeExtenstion from './createDefaultMeetingTypeExtension';
import getFirstTimeUser from '../selectors/getFirstTimeUser';
import setPageOrigin from '../actions/setPageOrigin';
import isFirstTimePersonalBookingUser from 'owa-pbp-option-publishing-settings/lib/selectors/isFirstTimePersonalBookingUser';
import type onboardingEntryPointEnum from './onboardingEntryPointEnum';
import { isFeatureEnabled } from 'owa-feature-flags';
import mapPublishingSettings from 'bookings-shared/lib/c1/mappers/mapPublishingSettings';
import updateBookingBusinessPage from 'bookings-shared/lib/c1/services/updateBookingBusinessPage';
import getFullUpdatedBookingBusinessEntryInput from 'bookings-shared/lib/c1/utils/getFullUpdatedBookingBusinessEntryInput';
import mapBookingBusinessEntryToPublishingSettings from 'bookings-shared/lib/c1/mappers/mapBookingBusinessEntryToPublishingSettings';
import type FeatureSource from 'bookings-shared/lib/c1/constants/source';
import { getUserConfiguration } from 'owa-session-store';
import getBookingBusiness from 'bookings-shared/lib/c1/services/getBookingBusiness';
import type { BookingBusinessEntry, BookingSettings } from 'owa-graph-schema';
import { setMasterPublished } from '../actions/setMasterPublished';

/**
 * Triggers a service call to update the personal booking page publishing status and mutate the store.
 */
export default async function setPublishedStatus(
    isPublished: boolean,
    isInitializingPublishedStatus?: boolean,
    onboardingEntryPoint?: onboardingEntryPointEnum // Needed to differentiate the bookable time onboarding or other init flow.
) {
    logUsage('setPublishedStatus', {
        entryPoint: getEntryPoint(),
        publish: isPublished,
        bookingMailboxPuid: getMailboxPuid(),
    });

    const isFirstTimeUser =
        onboardingEntryPoint === 'owaSlots' ? isFirstTimePersonalBookingUser() : getFirstTimeUser();

    const getOnboardingPageOrigin = () => {
        if (isFirstTimeUser) {
            return onboardingEntryPoint === 'owaSlots' ? 'owaSlots' : getEntryPoint();
        }
        return '';
    };

    // setting the paletteId to default value of 9 in case Theme is undefined
    let pbpPublishingSettings: PublishingSettings = {
        IsPublished: isPublished,
        Theme: { PaletteId: 9 }, // the default value is 9
        PageOrigin: getOnboardingPageOrigin(),
    };

    if (isFirstTimeUser) {
        // Set pageOrigin value in the local store also
        setPageOrigin(getOnboardingPageOrigin());
    }

    let paletteId: number;
    // override default value for paletteId if Theme is not undefined
    if (store.pbpPublishingSetting.publishingSettings.Theme) {
        paletteId = store.pbpPublishingSetting.publishingSettings.Theme.PaletteId;
        pbpPublishingSettings = {
            IsPublished: isPublished,
            Theme: { PaletteId: paletteId },
            PageOrigin: getOnboardingPageOrigin(),
        };
    }

    const perfDatapoint = new PerformanceDatapoint('setPublishedStatusV1');
    perfDatapoint.addCustomData({
        mailboxGuid: getUserMailboxGuid(),
        isB2: isFeatureEnabled('bookwithme-c1B2Migration'),
    });

    //B2 and excluding Bookable time flow for now
    if (isFeatureEnabled('bookwithme-c1B2Migration') && onboardingEntryPoint !== 'owaSlots') {
        // override default value for publishingSettings if not undefined

        pbpPublishingSettings = store.pbpPublishingSetting.publishingSettings;
        pbpPublishingSettings.IsPublished = isPublished;

        try {
            setIsSaving(true);
            //setPersonalScheduling
            const businessEntry = mapPublishingSettings(pbpPublishingSettings, 'BookWithMe');

            const bookingsSMTPAddress =
                getUserConfiguration().SessionSettings?.UserPrincipalName || '';
            const getBookingBusinessResponse = await getBookingBusiness(
                bookingsSMTPAddress,
                'BookWithMe'
            );
            const oldBusinessEntry =
                getBookingBusinessResponse?.businessInfo as BookingBusinessEntry;

            const updatedBookingBusinessEntryInput = getFullUpdatedBookingBusinessEntryInput(
                oldBusinessEntry,
                businessEntry
            );

            const response = await updateBookingBusinessPage(
                updatedBookingBusinessEntryInput,
                'BookWithMe'
            );
            if (!response.hasError && response.businessEntry) {
                const pbpSettings = mapBookingBusinessEntryToPublishingSettings(
                    response.businessEntry,
                    'BookWithMe'
                );
                setAnonymousSharingStatus(true);
                updatePublishingSettings(pbpSettings);
                const { publishingUrl } = response.businessEntry?.settings as BookingSettings;

                if (!!publishingUrl) {
                    setMasterPublished(true);
                    updatePublishingSettingsIsPublished(true);
                    setIsTurningOnBookingPage(true);
                } else {
                    setMasterPublished(false);
                    updatePublishingSettingsIsPublished(false);
                    setIsTurningOnBookingPage(false);
                }
                await getMeetingTypesFromServer({
                    publishedStatusAlreadySet: isInitializingPublishedStatus,
                });
                //Not required in B2
                //setPublishingSettingsExtension(isPublished, isInitializingPublishedStatus);
            } else {
                if (store.isTurningOnBookingPage) {
                    updatePublishingSettings(pbpPublishingSettings);
                    updatePublishingSettingsIsPublished(pbpPublishingSettings.IsPublished);
                    await getMeetingTypesFromServer({
                        publishedStatusAlreadySet: isInitializingPublishedStatus,
                    });
                    //Not required in B2
                    //setPublishingSettingsExtension(isPublished, isInitializingPublishedStatus);
                }
            }
        } catch (e) {
            perfDatapoint.endWithError(DatapointStatus.ServerError, e);
            trace.warn(e.message || 'setPublishedStatus failed');
        } finally {
            perfDatapoint.end();
            setIsSaving(false);
        }
    } else {
        try {
            setIsSaving(true);
            // If flight not enabled or is not the first run experience, then go the classic way to updating is published.
            perfDatapoint.addCheckmark('1-init: ' + pbpPublishingSettings.IsPublished);
            const setPersonalSchedulingPageResult = await setPersonalSchedulingPage(
                pbpPublishingSettings
            );

            if (!setPersonalSchedulingPageResult) {
                perfDatapoint.addCheckmark(
                    '2-failed setPersonalSchedulingPage: "already exists", retry later'
                );
                // If set personalScheduling page failed with AD error 'already exists' in description, ex:
                // "Directory operation did not succeed. Try again later., Active Directory operation failed ..
                // One or more attribute entries of the object '...,DC=...,DC=prod,DC=outlook,DC=com' already exists.",
                // "ResponseCode":"ErrorADOperation","ResponseClass":"Error"
                // Use the personal settings Extension IsPublished status stored in SDS as authoritative, valid for 2 hours after creation.
                if (store.isTurningOnBookingPage) {
                    updatePublishingSettings(pbpPublishingSettings);
                    /* if this is a first time user and we are initializing the publishedStatus then we do not want to create a loop by
                       calling setPublishedStatus from getMeetingTypesFromServer again so adding the optional param publishedStatusAlreadySet */
                    // TODO: refactor and refine loop issue and eliminate the optional param publishedStatusAlreadySet
                    await getMeetingTypesFromServer({
                        publishedStatusAlreadySet: isInitializingPublishedStatus,
                    }).then(() => {
                        setPublishingSettingsExtension(isPublished, isInitializingPublishedStatus);
                    });
                }
                // If error writing to AD will have to retry later.
                perfDatapoint.endWithError(DatapointStatus.ServerError);
                return;
            }

            perfDatapoint.addCheckpoint('3-succeeded setPersonalSchedulingPage');
            setAnonymousSharingStatus(!!setPersonalSchedulingPageResult.IsAnonymousSharingAllowed);
            pbpPublishingSettings.PublishedUrl =
                setPersonalSchedulingPageResult.FriendlyUrl ||
                setPersonalSchedulingPageResult.PublishedUrl;
            updatePublishingSettings(pbpPublishingSettings);

            const shouldCreateDefaultMeetingTypeExt = onboardingEntryPoint === 'bwmInitFlow';

            shouldCreateDefaultMeetingTypeExt &&
                (await createDefaultMeetingTypeExtenstion(
                    setPersonalSchedulingPageResult?.DefaultService
                ));

            /* if this is a first time user and we are initializing the publishedStatus then we do not want to create a loop by
               calling setPublishedStatus from getMeetingTypesFromServer again so adding the optional param publishedStatusAlreadySet */
            // TODO: refactor and refine loop issue and eliminate the optional param publishedStatusAlreadySet
            // Might not be needed, could be removed for future iteration.
            await getMeetingTypesFromServer({
                publishedStatusAlreadySet: isInitializingPublishedStatus,
                silent: true,
            }).then(() => {
                setPublishingSettingsExtension(isPublished, isInitializingPublishedStatus);
            });
        } catch (e) {
            perfDatapoint.endWithError(DatapointStatus.ServerError, e);
            trace.warn(e.message || 'setPublishedStatus failed');
        } finally {
            perfDatapoint.end();
            setIsSaving(false);
        }
    }
}

async function setPublishingSettingsExtension(
    isPublished: boolean,
    isInitializingPublishedStatus?: boolean
) {
    const perfDatapoint = new PerformanceDatapoint('setPublishingSettingsExtension');

    const getMeetingTypeExtensionSettingsResult: MeetingTypeExtensionResponse | null =
        await getMeetingTypeExtension(PUBLISHING_SETTINGS_ID);

    const isPublishedForAttendee =
        store.BookingPagePublishedStatus == PublishStatus.Complete ||
        store.BookingPagePublishedStatus == PublishStatus.Published;

    let attendeeAndOrganizerPublishSyncTime = null;
    if (isPublishedForAttendee) {
        attendeeAndOrganizerPublishSyncTime = owaDate();
    }

    const nowUtc = owaDate();
    const publishingSettingsExtension: PublishingSettingsExtension = {
        isPublishedForAttendee,
        attendeeAndOrganizerPublishSyncTime,
        isPublished,
        whenLastUpdated: nowUtc,
        whenSettingsCreated: nowUtc,
    };
    // Create/Update a meetingTypeExtension that has the publishingSettings.IsPublished status configuration.
    // The IsPublished status for the next hour will be honored from that cached copy, after one hour the authority
    // reverts to the substrate storage.
    // This is needed to solve the issue that when setting the publishing settings in substrate the value is saved to directory
    // but when retrieved is coming from a cached value that is not in sync with directory for almost 1 hour.
    if (
        isInitializingPublishedStatus ||
        !getMeetingTypeExtensionSettingsResult ||
        !getMeetingTypeExtensionSettingsResult?.additionalInformation
    ) {
        initializePublishSettings(isPublished);
    } else {
        // Get the saved publishingSettingsExtension
        const publishingSettingsExtensionOld = JSON.parse(
            getMeetingTypeExtensionSettingsResult.additionalInformation
        );

        // Override the save publishingSettingsExtension whenLastUpdated with now time
        publishingSettingsExtension.whenSettingsCreated =
            publishingSettingsExtensionOld.whenSettingsCreated ?? nowUtc;

        const publishingSettingsExtensionAdditionalInformation = JSON.stringify(
            publishingSettingsExtension
        );
        const publishingSettingsRequestBody: MeetingTypeExtension = {
            itemId: PUBLISHING_SETTINGS_ID,
            displayName: PUBLISHING_SETTINGS_NAME,
            isPrivate: true,
            recipients: [],
            additionalInformation: publishingSettingsExtensionAdditionalInformation,
            preBuffer: store.meetingTypeEditState.preBuffer,
            postBuffer: store.meetingTypeEditState.postBuffer,
            excludeFromSelfService: store.meetingTypeEditState.excludeFromSelfService,
        };
        await updateMeetingTypeExtension(publishingSettingsRequestBody);
    }

    // If auto publish new user page, then update the publishingSettings.IsPublished value in store before another initBookWithMeSettings call
    if (isInitializingPublishedStatus) {
        const publishingSettings = store.pbpPublishingSetting?.publishingSettings;
        if (!publishingSettings.IsPublished && isPublished) {
            // Updated the publishing settings in case there isa mismatch to substrate and isPublishedExtension is authoritative.
            updatePublishingSettingsIsPublished(isPublished);
        }
    }

    perfDatapoint.addCustomData({
        isPublished,
        entryPoint: getEntryPoint(),
        isInitializingPublishedStatus: isInitializingPublishedStatus
            ? 'publishingSettingsExtensionCreate'
            : 'publishingSettingsExtensionUpdate',
        whenLastUpdated: publishingSettingsExtension.whenLastUpdated.toString(),
        whenSettingsCreated: publishingSettingsExtension.whenSettingsCreated?.toString(),
        isPublishedForAttendee: publishingSettingsExtension.isPublishedForAttendee,
        attendeeAndOrganizerPublishSyncTime:
            publishingSettingsExtension.attendeeAndOrganizerPublishSyncTime?.toString(),
        isB2: isFeatureEnabled('bookwithme-c1B2Migration'),
    });
}
